import type { Workflow } from "@/stores/WorkflowStore";

import { WorkflowOutput, AgentConfigOutput } from "@webuild-ai/admin/models/components";
import { WorkflowConfig, AgentConfig } from "@webuild-ai/agent-platform/models/components";

// Helper function to determine if a workflow is specialized (not the default chat workflow)
export const isSpecializedWorkflow = (workflow: Workflow | null): boolean => {
  return workflow?.id !== "chat_agent";
};

/**
 * Ids of built-in workflows that exist for internal plumbing (the default
 * chat workflow and the Workflow Builder's build-agent generator) rather
 * than as a user-authored workflow. Callers that list or browse workflows
 * for selection should exclude these.
 */
export const SYSTEM_WORKFLOW_IDS = new Set(["chat_agent", "workflow_generator"]);

/**
 * Extracts the default prompt value from a workflow's input schema.
 * Returns null if no default value is defined.
 *
 * Schema example:
 * {
 *   "input": {
 *     "prompt": {
 *       "type": "string",
 *       "description": "Click Send to generate your report",
 *       "value": "Generate Report"  // ← This is the default value
 *     }
 *   }
 * }
 */
export const getWorkflowDefaultPrompt = (workflow: Workflow | null): string | null => {
  if (!workflow?.input) return null;

  // Check for prompt.value in the input schema
  const promptInput = workflow.input?.prompt;

  if (promptInput && typeof promptInput === "object" && "value" in promptInput) {
    return promptInput.value || null;
  }

  return null;
};

const convertAgentConfig = (adminAgent: AgentConfigOutput): AgentConfig => ({
  type: adminAgent.type,
  // Cast parameters - admin uses Array<any>, agent-platform uses Array<string>
  parameters: adminAgent.parameters as { [k: string]: string | number | Array<string> },
  triggers: adminAgent.triggers,
  userInteraction: adminAgent.userInteraction,
});

/**
 * Converts WorkflowOutput from admin API to WorkflowConfig for agent-platform API.
 *
 * This converter provides safe default values for the required fields.
 */
export const convertWorkflowOutputToConfig = (workflow: WorkflowOutput): WorkflowConfig => {
  // Convert agents
  const agents = Object.entries(workflow.agents).reduce(
    (acc, [key, agent]) => {
      acc[key] = convertAgentConfig(agent);

      return acc;
    },
    {} as { [k: string]: AgentConfig },
  );

  // Normalise the schedule. The two SDKs disagree about ScheduledConfig: admin
  // 1.13.0 has `{cron, values?, timezone?, enabled?}`, whereas agent-platform 1.4.0
  // still has only `{cron, values}` with values required. Defaulting values keeps the
  // platform happy, and assigning through a const rather than inlining the object
  // literal is what keeps `timezone` and `enabled` on the payload instead of tripping
  // excess property checks. Do not rebuild this object from cron and values alone,
  // which would strip the two fields the scheduler needs.
  const scheduled = workflow.scheduled
    ? { ...workflow.scheduled, values: workflow.scheduled.values ?? {} }
    : workflow.scheduled;

  return {
    name: workflow.name,
    id: workflow.id,
    description: workflow.description,
    chatManagerPrompt: workflow.chatManagerPrompt,
    scheduled,
    input: workflow.input,
    agents,
  };
};
